/**
 * Extra Material Service
 *
 * API for extra materials / homework links. Teachers/admins manage them;
 * students read the ones scoped to their level/class.
 */

import type {
  CreateExtraMaterialRequest,
  ExtraMaterial,
  UpdateExtraMaterialRequest,
} from "../types/material";
import api from "./api";

function toCamelCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toCamelCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    result[camelKey] =
      typeof value === "object" && value !== null
        ? toCamelCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

function toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    result[snakeKey] = value;
  }
  return result;
}

export async function getExtraMaterials(params?: {
  level?: string;
  classGroup?: string;
  scope?: string;
}): Promise<ExtraMaterial[]> {
  const response = await api.get("/extra-materials/", {
    params: {
      level: params?.level,
      class_group: params?.classGroup,
      scope: params?.scope,
    },
  });
  const data = Array.isArray(response.data)
    ? response.data
    : (response.data?.results ?? []);
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as ExtraMaterial[];
}

export async function createExtraMaterial(
  data: CreateExtraMaterialRequest,
): Promise<ExtraMaterial> {
  const response = await api.post(
    "/extra-materials/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as ExtraMaterial;
}

export async function updateExtraMaterial(
  id: string,
  data: UpdateExtraMaterialRequest,
): Promise<ExtraMaterial> {
  const response = await api.patch(
    `/extra-materials/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as ExtraMaterial;
}

export async function deleteExtraMaterial(id: string): Promise<void> {
  await api.delete(`/extra-materials/${id}/`);
}
